import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { User } from "lucide-react";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { useAuth } from "@/lib/auth";

const showError = (message: string, type: "error" | "success" | "info") => {
    //toast.error(message);
}

const getPlanLabel = (planType: string) => {
    switch (planType) {
        case "style":
            return "Style+";
        case "trial":
            return "Learn+ Trial";
        case "pro":
            return "Learn+";
        case "free":
            return "Free";
        default:
            return planType.charAt(0).toUpperCase() + planType.slice(1);
    }
}

const formatDate = (timestamp: number | null) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export const AccountTab = () => {
    const { authenticatedClient, sendMessage, getOptions, updateOptions } = useCommunication();
    const { authUser, planType, planEnd } = useAuth();

    const [username, setUsername] = useState("");
    const originalUsername = useRef(authUser?.app_metadata?.username || "");
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState("");

    useEffect(() => {
        setUsername(authUser?.app_metadata?.username || "");
        originalUsername.current = authUser?.app_metadata?.username || "";
    }, [authUser]);

    const handleDeleteAccount = async () => {
        const client = await authenticatedClient();
        if (!client) {
            showError("Something went wrong trying to authenticate you. Please try again.", "error");
            return;
        }
        const { error: deleteError } = await client.functions.invoke("delete-account");

        if (deleteError) {
            showError("Something went wrong trying to delete your account. Please try again.", "error");
            return;
        }

        showError("Your account has been deleted. We're sorry to see you go!", "info");

        sendMessage({
            type: "CALL_EXTENSION_FUNC",
            payload: {
                func: "updateSettings",
                payload: {
                    "auth": null,
                }
            },
        });
    }

    const handleEditName = () => {
        setEditedName(username);
        setIsEditingName(true);
    }

    const handleCancelEdit = () => {
        setIsEditingName(false);
        setEditedName("");
    }

    const handleSaveName = async () => {
        if (editedName === originalUsername.current) {
            setIsEditingName(false);
            return;
        }

        const client = await authenticatedClient();
        if (!client) {
            showError("Something went wrong trying to authenticate you. Please try again.", "error");
            return;
        }

        try {
            const { data, error: updateError } = await client.rpc("change_username", {
                new_username: editedName.replace(/\s/g, '_')
            });

            if (updateError) {
                showError("Couldn't update your username. It may already be taken.", "error");
                return;
            }

            if (data && Array.isArray(data) && data.length > 0 && data[0]?.success) {
                const options = getOptions();
                const newUsername = data[0].username;
                setUsername(newUsername);
                originalUsername.current = newUsername;
                setIsEditingName(false);
                updateOptions({
                    "auth": {
                        ...options?.auth,
                        user: {
                            ...options?.auth?.user,
                            app_metadata: {
                                ...options?.auth?.user?.app_metadata,
                                username: newUsername
                            }
                        }
                    },
                });
                showError("Username updated successfully!", "success");
            } else {
                const errorMessage = (data && Array.isArray(data) && data[0]?.message) || "Couldn't update your username. It may already be taken.";
                showError(errorMessage, "error");
            }
        } catch (err) {
            showError("An error occurred while updating your username. Please try again.", "error");
        }
    }

    const avatarUrl = authUser?.user_metadata?.avatar_url;
    const hasPlan = planType && planType !== "none";

    return (
        <div className="flex flex-col gap-0 p-4 min-h-full bg-theme-0">
            {/* Profile Header Card */}
            <div className="mb-6 bg-theme-0">
                <div className="size-20 flex items-center justify-center overflow-hidden rounded-full border border-theme mb-4">
                    {avatarUrl ? (
                        <img src={avatarUrl} alt="Profile" className="size-full object-cover" />
                    ) : (
                        <User className="size-8 text-theme-1" />
                    )}
                </div>
                <div className="flex items-baseline gap-2">
                    <h2 className="text-2xl font-semibold text-theme-0">{authUser?.user_metadata?.full_name || "User"}</h2>
                    <span className="text-sm text-theme-1">(@{username || "username"})</span>
                </div>
                <p className="text-sm text-theme-1 mt-0.5">{authUser?.email || ""}</p>

                {hasPlan && (
                    <div className="mt-4 bg-theme-1 rounded-lg px-4 py-3 flex items-center gap-3">
                        <span className="bg-theme-0 text-theme-0 text-xs font-semibold px-2 py-1 rounded">
                            {getPlanLabel(planType)}
                        </span>
                        <span className="text-sm text-theme-0">
                            You're on the {getPlanLabel(planType)} plan.{planEnd && planType !== "free" ? ` Renewing on ${formatDate(planEnd)}.` : ""}
                        </span>
                    </div>
                )}
            </div>

            {/* Personal Details Section */}
            <div>
                <h3 className="text-lg font-semibold text-theme-0">Personal details</h3>

                {/* Full Name Row */}
                <div className="py-4 border-b border-theme">
                    <div>
                        <p className="text-sm font-semibold text-theme-0">Full name</p>
                        <p className="text-sm text-theme-1 mt-0.5">{authUser?.user_metadata?.full_name || "Not set"}</p>
                    </div>
                </div>

                {/* Username Row */}
                <div className="py-4 border-b border-theme">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-theme-0">Username</p>
                            {isEditingName ? (
                                <div className="mt-2 flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={editedName}
                                        onChange={(e) => setEditedName(e.target.value.replace(/\s/g, '_'))}
                                        className="h-[36px] flex-1 border border-theme rounded-lg px-3 bg-theme-0 text-theme-0"
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleCancelEdit}
                                        className="text-sm text-theme-1 hover:text-theme-0 font-bold"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSaveName}
                                        className="text-sm text-brand font-bold"
                                    >
                                        Save
                                    </button>
                                </div>
                            ) : (
                                <p className="text-sm text-theme-1 mt-0.5">{username || "Not set"}</p>
                            )}
                        </div>
                        {!isEditingName && (
                            <button
                                onClick={handleEditName}
                                className="text-sm text-theme-0 hover:text-theme-1 font-bold"
                            >
                                Edit
                            </button>
                        )}
                    </div>
                </div>

                {/* Email Row */}
                <div className="py-4">
                    <p className="text-sm font-semibold text-theme-0">Email</p>
                    <p className="text-sm text-theme-1 mt-0.5">{authUser?.email || "Not set"}</p>
                </div>
            </div>

            {/* Manage Account Section */}
            <div className="mt-6">
                <h3 className="text-lg font-semibold text-theme-0 mb-4">Manage account</h3>
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-sm font-bold text-theme-0">Delete account</p>
                        <p className="text-sm text-theme-1 mt-0.5">Permanently delete your BetterCanvas account.</p>
                    </div>
                    <Dialog>
                        <DialogTrigger asChild>
                            <button className="text-sm text-red-500 font-bold cursor-pointer hover:text-red-600">
                                Delete
                            </button>
                        </DialogTrigger>
                        <DialogContent className="w-[400px] bg-theme-0 border-theme">
                            <DialogHeader>
                                <DialogTitle>Delete Account</DialogTitle>
                                <DialogDescription>
                                    Are you sure you want to delete your account? This action is irreversible.
                                </DialogDescription>
                            </DialogHeader>
                            <button className="bg-red-500 text-white px-4 py-2 rounded-md" onClick={handleDeleteAccount}>Delete Account</button>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

        </div>
    )
}
